import React, { useLayoutEffect, useRef } from 'react';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { MapPin, Clock, Phone, Send, Navigation, Star, ShieldCheck } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const BranchesPage = () => {
  const containerRef = useRef(null);

  const branches = [
    {
      name: "Vanasthalipuram Main Hospital",
      type: "Main Hospital & Surgical Center",
      tag: "Primary Location",
      tagColor: "bg-primary text-white",
      address: "Beside Line Of Vivekanandha Park, Hyderabad, 500070",
      hours: "09:00 AM — 09:00 PM",
      availText: "Everyday · Emergency 24/7",
      phone: "+91 9848954921",
      mapUrl: "https://maps.app.goo.gl/1KD7SS8UeyVASEJq9",
      features: ["Full Surgical OT", "In-House Laboratory", "ICU & Emergency", "Digital X-Ray"],
      featured: true,
    },
    {
      name: "SR Pet clinic",
      type: "Clinic — Hasthinapuram",
      tag: "Wellness Branch",
      tagColor: "bg-blue-500 text-white",
      address: "Hasthinapuram Central, Hyderabad",
      hours: "09:30 AM — 09:00 PM",
      availText: "Monday — Sunday",
      phone: "+91 9848954921",
      mapUrl: "https://maps.app.goo.gl/AitGp4EtcSMpVQQd9",
      features: ["Wellness Exams", "Vaccination", "Grooming", "Minor Procedures"],
      featured: false,
    },
    {
      name: "Pet Planet Dog Clinic, Turkayamjal",
      type: "Satellite Clinic",
      tag: "Satellite Clinic",
      tagColor: "bg-purple-500 text-white",
      address: "Turkayamjal, Hyderabad",
      hours: "10:00 AM — 08:30 PM",
      availText: "Monday — Saturday",
      phone: "+91 9848954921",
      mapUrl: "https://maps.app.goo.gl/AngNqHp4njfe8crS6",
      features: ["Consultation", "Vaccination", "Parasite Control", "Basic Diagnostics"],
      featured: false,
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.branch-hero-line', { y: 60, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2 });
      gsap.fromTo('.branch-card', { y: 50, opacity: 0, scale: 0.97 }, {
        y: 0, opacity: 1, scale: 1, stagger: 0.15, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: '.branches-grid', start: 'top 75%' }
      });
      gsap.to('.hero-glow', { y: '-15%', ease: 'none', scrollTrigger: { trigger: '.branches-hero', scrub: true } });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO 
        title="Our Clinical Branches"
        description="Locate a Dr. Suresh Pet Care facility near you in Vanasthalipuram, Hasthinapuram, and Turkayamjal."
        canonical="/branches"
      />
      <motion.div ref={containerRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white overflow-hidden">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="branches-hero pt-[140px] md:pt-[160px] pb-20 relative overflow-hidden min-h-[70vh] flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/5 z-0" />
        <div className="hero-glow absolute top-[10%] right-[5%] w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-[10%] w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="branch-hero-line inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/15 mb-10">
            <MapPin size={13} className="text-primary" />
            <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">The DR. SURESH Network</span>
          </div>
          <h1 className="branch-hero-line text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(3rem,8vw,6.5rem)] mb-8 max-w-4xl">
            Our Clinical <br /><em className="font-normal text-primary not-italic">Branches.</em>
          </h1>
          <p className="branch-hero-line text-muted-foreground text-xl md:text-2xl leading-relaxed max-w-2xl mb-10">
            Three strategically located sanctuaries across Hyderabad — bringing world-class veterinary care closer to your neighborhood.
          </p>
          <div className="branch-hero-line flex flex-wrap gap-4">
            <div className="flex items-center gap-2 px-5 py-3 bg-white border border-border rounded-full text-lg font-bold text-navy shadow-sm">
              <Star size={18} className="text-primary fill-primary" /> Vanasthalipuram
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white border border-border rounded-full text-lg font-bold text-navy shadow-sm">
              <Star size={18} className="text-blue-500 fill-blue-500" /> Hasthinapuram
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-white border border-border rounded-full text-lg font-bold text-navy shadow-sm">
              <Star size={18} className="text-purple-500 fill-purple-500" /> Turkayamjal
            </div>
          </div>
        </div>
      </section>

      {/* ── BRANCH CARDS ─────────────────────────────────── */}
      <section className="py-16 bg-[#f8faff]">
        <div className="section-container">
          <div className="branches-grid grid lg:grid-cols-3 gap-6">
            {branches.map((branch, i) => (
              <div key={i} className={`branch-card group relative rounded-[3rem] overflow-hidden border transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 ${branch.featured ? 'bg-[#010816] border-white/10 lg:col-span-1 lg:row-span-1' : 'bg-white border-navy/5'}`}>
                {/* Glow for featured */}
                {branch.featured && (
                  <>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-primary/15 rounded-full blur-[80px] pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-900/20 rounded-full blur-[80px] pointer-events-none" />
                  </>
                )}

                <div className="relative z-10 p-10 h-full flex flex-col">
                  {/* Tag */}
                  <div className="flex items-start justify-between mb-8">
                    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${branch.tagColor}`}>{branch.tag}</span>
                    {branch.featured && <ShieldCheck size={20} className="text-primary" />}
                  </div>

                  {/* Name & Type */}
                  <h2 className={`text-2xl font-bold mb-2 group-hover:text-primary transition-colors duration-500 ${branch.featured ? 'text-white' : 'text-navy'}`}>{branch.name}</h2>
                  <p className={`text-sm font-bold uppercase tracking-[0.15em] mb-6 ${branch.featured ? 'text-primary' : 'text-muted-foreground'}`}>{branch.type}</p>

                  {/* Details */}
                  <div className="space-y-4 mb-8 flex-1">
                    <div className={`flex items-start gap-3 text-sm ${branch.featured ? 'text-white/70' : 'text-muted-foreground'}`}>
                      <MapPin size={15} className="text-primary mt-0.5 shrink-0" />
                      <span className="leading-relaxed">{branch.address}</span>
                    </div>
                    <div className={`flex items-center gap-3 text-sm ${branch.featured ? 'text-white/70' : 'text-muted-foreground'}`}>
                      <Clock size={15} className="text-primary shrink-0" />
                      <div>
                        <span className="font-semibold">{branch.hours}</span>
                        <span className={`block text-xs mt-0.5 ${branch.featured ? 'text-primary' : 'text-primary'}`}>{branch.availText}</span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-3 text-sm ${branch.featured ? 'text-white/70' : 'text-muted-foreground'}`}>
                      <Phone size={15} className="text-primary shrink-0" />
                      <a href={`tel:${branch.phone}`} className="hover:text-primary transition-colors font-semibold">{branch.phone}</a>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2 mb-8">
                    {branch.features.map((f, j) => (
                      <div key={j} className={`px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wide ${branch.featured ? 'bg-white/8 text-white/70 border border-white/10' : 'bg-primary/5 text-primary border border-primary/10'}`}>
                        {f}
                      </div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex gap-3">
                    {branch.mapUrl && (
                      <a href={branch.mapUrl} target="_blank" rel="noopener noreferrer"
                        className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all duration-500 ${branch.featured ? 'bg-primary text-white hover:bg-primary/90 hover:shadow-[0_10px_30px_rgba(3,137,3,0.4)]' : 'bg-navy text-white hover:bg-navy/90'}`}>
                        <Navigation size={13} /> Get Directions
                      </a>
                    )}
                    <a href={`https://wa.me/919848954921?text=${encodeURIComponent(`Hi Dr. Suresh, I would like to book an appointment for my pet at the ${branch.name} branch.`)}`} target="_blank" rel="noopener noreferrer"
                      className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all duration-500 ${branch.featured ? 'bg-white/10 text-white border border-white/15 hover:bg-white/20' : 'bg-primary/5 text-primary border border-primary/15 hover:bg-primary/10'}`}>
                      <Send size={13} /> Book Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NETWORK STATS ─────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { value: '3', label: 'Locations Across Hyderabad', sub: 'Strategically located' },
              { value: '23+', label: 'Years of Clinical Legacy', sub: 'Led by Dr. T Suresh Babu' },
              { value: '24/7', label: 'Emergency Coverage', sub: 'At Vanasthalipuram Main' },
            ].map((s, i) => (
              <div key={i} className="p-10 bg-[#f8faff] rounded-[2.5rem] border border-navy/5 hover:border-primary/20 hover:shadow-lg transition-all duration-500">
                <p className="text-5xl md:text-6xl font-bold text-navy mb-3">{s.value}</p>
                <p className="font-bold text-navy text-base mb-1">{s.label}</p>
                <p className="text-xs text-muted-foreground">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT CTA ─────────────────────────────────── */}
      <section className="py-16 pb-24">
        <div className="section-container">
          <div className="relative rounded-[4rem] overflow-hidden bg-[#010816] p-12 md:p-20 text-center">
            <div className="absolute top-0 left-[20%] w-[600px] h-[300px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-8">
                <Phone size={13} className="text-primary" />
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-white/60">Emergency & Bookings</span>
              </div>
              <h2 className="text-white font-bold text-4xl md:text-5xl mb-6">
                Visit Any Branch, <br /><em className="font-normal text-primary not-italic">Experience Excellence.</em>
              </h2>
              <p className="text-white/60 text-lg mb-10">All locations are staffed by qualified veterinary professionals. Walk in or book ahead for priority access.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://wa.me/919848954921" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-black text-sm tracking-widest hover:shadow-[0_20px_40px_rgba(3,137,3,0.4)] transition-all duration-500">
                  <Send size={16} /> WhatsApp Booking
                </a>
                <a href="tel:+919848954921"
                  className="inline-flex items-center gap-3 bg-white/5 text-white border border-white/10 px-10 py-4 rounded-full font-black text-sm tracking-widest hover:bg-white/10 transition-all duration-500">
                  <Phone size={16} className="text-primary" /> +91 9848954921
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      </motion.div>
    </>
  );
};

export default BranchesPage;
